const IMAGE_REFERENCE_RE = /([【\[])?(image|图片|图)\s*([0-9０-９]+|[一二两三四五六七八九十]+)([】\]])?/giu;

const CHINESE_DIGIT_VALUES = new Map([
  ['一', 1],
  ['二', 2],
  ['两', 2],
  ['三', 3],
  ['四', 4],
  ['五', 5],
  ['六', 6],
  ['七', 7],
  ['八', 8],
  ['九', 9],
]);

function buildImageToken(index) {
  return `image${index}`;
}

function parseImageTokenIndex(token) {
  const match = String(token || '').trim().toLowerCase().match(/^image([1-9]\d*)$/);
  if (!match) {
    return null;
  }
  const parsed = Number(match[1]);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function normalizeAvailableTokenSet(availableTokens) {
  return new Set((availableTokens || [])
    .map((token) => String(token).trim().toLowerCase())
    .filter(Boolean));
}

function normalizeDigits(value) {
  return value.replace(/[０-９]/g, (char) => String(char.charCodeAt(0) - 0xff10));
}

function parsePositiveInteger(value) {
  const normalizedValue = normalizeDigits(value);
  if (!/^\d+$/.test(normalizedValue)) {
    return null;
  }

  const parsed = Number(normalizedValue);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function parseChineseNumber(value) {
  if (!value) {
    return null;
  }

  if (CHINESE_DIGIT_VALUES.has(value)) {
    return CHINESE_DIGIT_VALUES.get(value);
  }

  const tenParts = value.split('十');
  if (tenParts.length !== 2) {
    return null;
  }

  const [left, right] = tenParts;
  const tens = left ? CHINESE_DIGIT_VALUES.get(left) : 1;
  const ones = right ? CHINESE_DIGIT_VALUES.get(right) : 0;
  if (!tens || ones == null) {
    return null;
  }

  return tens * 10 + ones;
}

function splitArabicDigitSequence(value) {
  const normalizedValue = normalizeDigits(value);
  if (!/^[1-9]{2,}$/.test(normalizedValue)) {
    return null;
  }

  return normalizedValue.split('').map((digit) => buildImageToken(Number(digit)));
}

function splitChineseDigitSequence(value) {
  if (value.length < 2 || !Array.from(value).every((char) => CHINESE_DIGIT_VALUES.has(char))) {
    return null;
  }

  return Array.from(value).map((char) => buildImageToken(CHINESE_DIGIT_VALUES.get(char)));
}

function resolveReferenceTokens(prefix, rawValue, availableTokenSet) {
  const normalizedPrefix = String(prefix || '').toLowerCase();
  const isEnglishImagePrefix = normalizedPrefix === 'image';
  const directNumber = parsePositiveInteger(rawValue) ?? parseChineseNumber(rawValue);
  const directToken = directNumber ? buildImageToken(directNumber) : null;

  if (!isEnglishImagePrefix) {
    const splitTokens = splitArabicDigitSequence(rawValue) || splitChineseDigitSequence(rawValue);
    if (splitTokens && (!directToken || !availableTokenSet.has(directToken))) {
      return splitTokens;
    }
  }

  return directToken ? [directToken] : [];
}

function extractExploreImageReferenceTokens(text, availableTokens) {
  if (typeof text !== 'string' || !text.trim()) {
    return [];
  }

  const availableTokenSet = normalizeAvailableTokenSet(availableTokens);
  const tokens = [];
  const seen = new Set();

  for (const match of text.matchAll(IMAGE_REFERENCE_RE)) {
    const referenceTokens = resolveReferenceTokens(match[2], match[3], availableTokenSet);
    referenceTokens.forEach((token) => {
      if (!seen.has(token)) {
        seen.add(token);
        tokens.push(token);
      }
    });
  }

  return tokens;
}

function buildReferenceText(prefix, index) {
  return `${prefix}${index}`;
}

function appendExploreImageReferenceToken(text, token, availableTokens) {
  const availableTokenSet = normalizeAvailableTokenSet(availableTokens);
  const normalizedToken = String(token || '').trim().toLowerCase();
  const tokenIndex = parseImageTokenIndex(normalizedToken);
  if (!tokenIndex || (availableTokenSet.size > 0 && !availableTokenSet.has(normalizedToken))) {
    return typeof text === 'string' ? text : '';
  }

  const sourceText = typeof text === 'string' ? text : '';
  const existingTokens = extractExploreImageReferenceTokens(sourceText, availableTokens)
    .map((existingToken) => existingToken.toLowerCase());
  if (existingTokens.includes(normalizedToken)) {
    return sourceText;
  }

  const referenceText = `【${buildImageToken(tokenIndex)}】`;
  const trimmedText = sourceText.trim();
  return trimmedText ? `${trimmedText} ${referenceText}` : referenceText;
}

function remapExploreImageReferenceTokens(text, previousTokens, nextTokenByPreviousToken) {
  if (typeof text !== 'string' || !text.trim()) {
    return text;
  }

  const previousTokenSet = normalizeAvailableTokenSet(previousTokens);
  const normalizedTokenMap = new Map(
    Object.entries(nextTokenByPreviousToken || {}).map(([fromToken, toToken]) => [
      String(fromToken).trim().toLowerCase(),
      toToken ? String(toToken).trim().toLowerCase() : null,
    ]),
  );

  return text.replace(IMAGE_REFERENCE_RE, (fullMatch, open, prefix, rawValue, close) => {
    const referenceTokens = resolveReferenceTokens(prefix, rawValue, previousTokenSet);
    if (referenceTokens.length === 0) {
      return fullMatch;
    }

    const mappedReferences = referenceTokens.flatMap((token) => {
      if (!normalizedTokenMap.has(token)) {
        return [fullMatch];
      }
      const nextToken = normalizedTokenMap.get(token);
      if (!nextToken) {
        return [];
      }
      const nextIndex = parseImageTokenIndex(nextToken);
      if (!nextIndex) {
        return [fullMatch];
      }
      return [buildReferenceText(prefix, nextIndex)];
    });

    if (mappedReferences.length === 0) {
      return '';
    }

    const joinedReference = mappedReferences.join(' ');
    return `${open || ''}${joinedReference}${close || ''}`;
  }).replace(/[ \t]{2,}/g, ' ').trim();
}

function resolveExploreReferenceDraftAfterReorder(
  text,
  previousTokens,
  nextTokenByPreviousToken,
  canonicalAutoPrompt,
) {
  if (typeof canonicalAutoPrompt === 'string' && canonicalAutoPrompt.trim()) {
    return canonicalAutoPrompt;
  }
  return remapExploreImageReferenceTokens(text, previousTokens, nextTokenByPreviousToken);
}

module.exports = {
  appendExploreImageReferenceToken,
  extractExploreImageReferenceTokens,
  remapExploreImageReferenceTokens,
  resolveExploreReferenceDraftAfterReorder,
};
